import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Stack, useTheme } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";

import Button from "components/basic/button";
import Typography from "components/basic/typography";
import { convertHeicToJpeg } from "utils/heic";

import { BookingAttachment } from "../../../../types";

interface AttachmentsSectionProps {
  existingAttachments: BookingAttachment[];
  newFiles: File[];
  onAddFiles: (files: File[]) => void;
  onRemoveExisting: (index: number) => void;
  onRemoveNew: (index: number) => void;
}

const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({
  existingAttachments,
  newFiles,
  onAddFiles,
  onRemoveExisting,
  onRemoveNew,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");
  const [converting, setConverting] = useState(false);

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) {
      return;
    }
    const picked = Array.from(files);
    e.target.value = "";

    setConverting(true);
    try {
      onAddFiles(await Promise.all(picked.map(convertHeicToJpeg)));
    } finally {
      setConverting(false);
    }
  };

  const attachmentRow = (key: string, name: string, onRemove: () => void) => (
    <Stack
      key={key}
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        backgroundColor: theme.palette.grey[100],
        borderRadius: 1,
        px: 1,
        py: 0.5,
      }}
    >
      <InsertDriveFileIcon
        sx={{ fontSize: 16, color: theme.palette.action.active }}
      />
      <Typography variant="body2" style={{ flex: 1 }}>
        {name}
      </Typography>
      <IconButton size="small" onClick={onRemove}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Stack>
  );

  return (
    <Box>
      <Button
        variant="outlined"
        color="primary"
        component="label"
        disabled={converting}
        startAdornment={<AttachFileIcon fontSize="small" />}
      >
        {converting ? t("booking.converting") : t("booking.attach")}
        <input hidden type="file" multiple onChange={handleFiles} />
      </Button>
      {(existingAttachments.length > 0 || newFiles.length > 0) && (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {existingAttachments.map((a, idx) =>
            attachmentRow(`e-${idx}`, a.name, () => onRemoveExisting(idx)),
          )}
          {newFiles.map((f, idx) =>
            attachmentRow(`n-${idx}`, f.name, () => onRemoveNew(idx)),
          )}
        </Stack>
      )}
    </Box>
  );
};

export default AttachmentsSection;
